
import React from 'react';
import { useDrag } from 'react-dnd';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { GripVertical, User, Hash, Link, ArrowUp, ArrowDown } from "lucide-react";

interface DraggableRecordCardProps {
  id: string;
  creatorName: string;
  handler: string;
  type: string;
  isPending: boolean;
  onLinkAsParent: () => void;
  onLinkAsChild: () => void;
  isProcessing: boolean;
}

export const DraggableRecordCard: React.FC<DraggableRecordCardProps> = ({
  id,
  creatorName,
  handler,
  type,
  isPending,
  onLinkAsParent,
  onLinkAsChild,
  isProcessing
}) => {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: 'RECORD',
    item: { id, creatorName, handler, type },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  }));

  return (
    <Card 
      ref={drag}
      className={`cursor-move transition-all duration-200 ${
        isDragging ? 'opacity-50 scale-95' : 'hover:shadow-md'
      } ${isPending ? 'ring-2 ring-blue-400 bg-blue-50 dark:bg-blue-900/20' : ''}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <GripVertical className="h-5 w-5 text-gray-400 mt-1 flex-shrink-0" />
          
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <User className="h-4 w-4 text-gray-500" />
              <h4 className="font-medium text-sm truncate">{creatorName}</h4>
              {isPending && (
                <Badge variant="secondary" className="text-xs">
                  Processing...
                </Badge>
              )}
            </div>
            
            <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
              <div className="flex items-center gap-1">
                <Hash className="h-3 w-3" />
                <span className="truncate">{handler || 'No handler'}</span>
              </div>
              <div className="flex items-center gap-1">
                <Badge variant="outline" className="text-xs px-1 py-0">
                  {type || 'Unknown'}
                </Badge>
              </div>
            </div>
            
            <div className="flex gap-1 mt-3">
              <Button
                size="sm"
                variant="outline"
                onClick={onLinkAsParent}
                disabled={isProcessing || isPending}
                className="text-xs px-2 py-1 h-auto"
              >
                <ArrowUp className="h-3 w-3 mr-1" />
                Parent
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={onLinkAsChild}
                disabled={isProcessing || isPending}
                className="text-xs px-2 py-1 h-auto"
              >
                <ArrowDown className="h-3 w-3 mr-1" />
                Child
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
